import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Logo from "../../atoms/Logo/Logo.component";
import Button from "../../atoms/Button/Button.component";
import Icon from "../../atoms/Icon/Icon.component";
import { useScrollLock } from "../../../utils/useScrollLock";
import {
  getLandingMenuLinks,
  TENDBE_APP_URL,
} from "../../../constants/navigation";
import {
  BrandLink,
  HeaderWrapper,
  MenuToggle,
  Nav,
  NavActions,
  NavLink,
  NavLinks,
} from "./LandingHeader.styled";

const MOBILE_NAV_ID = "landing-mobile-nav";

const LandingHeader = () => {
  const { t } = useTranslation();
  const [isMenuOpened, setIsMenuOpened] = useState(false);
  const [isCompact, setIsCompact] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const menuLinks = getLandingMenuLinks(t);

  // Switches to the compact bar once the visitor scrolls past the hero -
  // see the #hero-sentinel marker rendered at the top of LandingHero's
  // trust strip.
  useEffect(() => {
    const sentinel = document.getElementById("hero-sentinel");
    if (!sentinel || typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsCompact(!entry.isIntersecting)
    );
    observer.observe(sentinel);

    return () => observer.disconnect();
  }, []);

  // Keeps --sticky-header-h in sync with the bar's real height, so anchor
  // links (scroll-padding-top, see globalStyles) never land underneath it.
  useEffect(() => {
    const node = headerRef.current;
    if (!node || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const setHeightVar = () => {
      document.documentElement.style.setProperty(
        "--sticky-header-h",
        `${node.offsetHeight}px`
      );
    };

    setHeightVar();
    const observer = new ResizeObserver(setHeightVar);
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  // Mobile menu: closes on Escape and locks background scroll while open.
  useScrollLock(isMenuOpened);

  useEffect(() => {
    if (!isMenuOpened) {
      return undefined;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsMenuOpened(false);
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isMenuOpened]);

  return (
    <HeaderWrapper ref={headerRef} $compact={isCompact}>
      <Nav as="div" isOpen={isMenuOpened} $compact={isCompact}>
        <BrandLink to="/" aria-label={t("landing.brand.home")}>
          <Logo
            width={isCompact ? "104px" : "130px"}
            tagline={isCompact ? undefined : t("landing.brand.tagline")}
          />
        </BrandLink>
        <NavLinks
          as="nav"
          id={MOBILE_NAV_ID}
          isOpen={isMenuOpened}
          aria-label={t("landing.nav.label")}
        >
          {menuLinks.map((menuItem) => (
            <NavLink
              key={menuItem.link}
              to={{ pathname: "/", hash: menuItem.link }}
              onClick={() => setIsMenuOpened(false)}
            >
              {menuItem.title}
            </NavLink>
          ))}
        </NavLinks>
        <NavActions $compact={isCompact}>
          <Button href={TENDBE_APP_URL}>{t("landing.hero.startFree")}</Button>
          <MenuToggle
            type="button"
            aria-controls={MOBILE_NAV_ID}
            aria-expanded={isMenuOpened}
            aria-label={
              isMenuOpened
                ? t("landing.nav.closeMenu")
                : t("landing.nav.openMenu")
            }
            onClick={() => setIsMenuOpened(!isMenuOpened)}
          >
            <Icon name={isMenuOpened ? "close" : "menu"} />
          </MenuToggle>
        </NavActions>
      </Nav>
    </HeaderWrapper>
  );
};

export default LandingHeader;
